import React, { useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Skeleton,
  Stack,
  Text,
  Badge,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
} from "@chakra-ui/react";
import {
  FaShoppingCart,
  FaBolt,
  FaStar,
  FaShieldAlt,
  FaUndo,
  FaFileInvoice,
  FaTruck,
  FaCheck,
  FaShareAlt,
} from "react-icons/fa";
import { ChevronRightIcon } from "@chakra-ui/icons";
import ReactImageMagnify from "react-image-magnify";
import { Link, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { CartContext } from "../Context/CartContext";
import { Authcontext } from "../Context/Authcontext";
import { apiUrl } from "../ApiUrl/ApiUrl";

function Viewpage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [viewData, setViewData] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const { getData } = useContext(CartContext);
  const { correct } = useContext(Authcontext);
  const { item_id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [item_id]);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(`${apiUrl}/public/singleBook?bid=${item_id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setViewData(Array.isArray(data) ? data : [data]);
    } catch (err) {
      console.error("Error fetching product data:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const addDatainCart = async (redirectOnSuccess = false) => {
    const uid = localStorage.getItem("uid");
    if (!uid) {
      Swal.fire({
        title: "Please Sign In",
        text: "You need to be logged in to add items to your cart.",
        icon: "info",
        confirmButtonText: "Sign In",
        confirmButtonColor: "#0F172A",
      });
      return;
    }

    setIsAddingToCart(true);

    try {
      const item = viewData[0] || {};
      const res = await fetch(`${apiUrl}/public/addcart`, {
        method: "POST",
        body: JSON.stringify({ ...item, uid: uid }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to add item to cart");
      }

      await res.json();
      await getData();

      if (redirectOnSuccess) {
        navigate("/cart");
      } else {
        toast({
          title: "Added to Cart!",
          description: `${item.booktitle || "Item"} has been added to your shopping cart.`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      Swal.fire({
        title: "Cart Notice",
        text: err.message || "Could not add item to cart.",
        icon: "warning",
        confirmButtonColor: "#0F172A",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleAddToCart = () => {
    addDatainCart(false);
  };

  const handleBuyNow = () => {
    addDatainCart(true);
  };

  if (loading) {
    return (
      <Box maxW="1360px" mx="auto" px={{ base: "4", md: "8" }} py="8">
        <Grid templateColumns={{ base: "1fr", lg: "450px 1fr" }} gap="10">
          <Skeleton height="450px" rounded="2xl" />
          <Stack spacing="4">
            <Skeleton height="35px" width="70%" rounded="lg" />
            <Skeleton height="25px" width="40%" rounded="lg" />
            <Skeleton height="45px" width="50%" rounded="lg" />
            <Skeleton height="150px" rounded="xl" />
            <Skeleton height="200px" rounded="xl" />
          </Stack>
        </Grid>
      </Box>
    );
  }

  if (error || viewData.length === 0) {
    return (
      <Box maxW="600px" mx="auto" py="16" px="4">
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          rounded="2xl"
          p="8"
          shadow="sm"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt="4" mb="1" fontSize="lg">
            Publication Details Unavailable
          </AlertTitle>
          <AlertDescription maxWidth="sm" color="gray.600" mb="5">
            We couldn't load the details for this publication. Please refresh the page or return to the catalog.
          </AlertDescription>
          <Link to="/products?maincat=Books">
            <Button colorScheme="blue" size="md">
              Back to Catalog
            </Button>
          </Link>
        </Alert>
      </Box>
    );
  }

  const item = viewData[0] || {};
  const originalPrice = parseFloat(item.originalprice) || 0;
  const descPrice = parseFloat(item.descprice) || 0;
  const discountPct =
    originalPrice > descPrice && originalPrice > 0
      ? Math.round(((originalPrice - descPrice) / originalPrice) * 100)
      : 0;

  return (
    <Box minH="85vh" bg="#F8FAFC" py={{ base: "4", md: "8" }} px={{ base: "3", md: "6", lg: "8" }}>
      <Box maxW="1360px" mx="auto">
        {/* Breadcrumb */}
        <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.400" />} fontSize="13px" color="gray.500" mb="6">
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={`/products?maincat=${item.maincatname || "Books"}`}>
              {item.maincatname || "Publications"}
            </BreadcrumbLink>
          </BreadcrumbItem>
          {item.subcatname && (
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/products?maincat=Books&subcatname=${encodeURIComponent(item.subcatname)}`}>
                {item.subcatname}
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink fontWeight="bold" color="gray.800" noOfLines={1} maxW="280px">
              {item.booktitle}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* 2-Column Product Layout */}
        <Grid templateColumns={{ base: "1fr", lg: "480px 1fr" }} gap={{ base: 6, lg: 10 }} alignItems="start">
          {/* Left Column: Image Showcase & Sticky Purchase Buttons */}
          <Box position={{ lg: "sticky" }} top="100px">
            <Box
              bg="white"
              border="1px solid #E2E8F0"
              rounded="2xl"
              p="6"
              shadow="sm"
              textAlign="center"
              overflow="hidden"
              mb="4"
            >
              <Box
                h={{ base: "280px", sm: "360px", md: "420px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
              >
                <ReactImageMagnify
                  {...{
                    smallImage: {
                      alt: item.booktitle || "Product Image",
                      isFluidWidth: true,
                      src: item.imagePath || "https://navneetlawstudio.com/logo.png",
                    },
                    largeImage: {
                      src: item.imagePath || "https://navneetlawstudio.com/logo.png",
                      width: 1200,
                      height: 1800,
                    },
                    enlargedImageContainerDimensions: {
                      width: "180%",
                      height: "100%",
                      border: "1px solid #CBD5E1",
                      borderRadius: "12px",
                      boxShadow: "0 20px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* CTAs: Add to Cart & Buy Now */}
            <Flex gap="3" align="center">
              {correct ? (
                <>
                  <Button
                    flex="1"
                    size="lg"
                    h="52px"
                    bg="#0F172A"
                    color="white"
                    rounded="xl"
                    fontSize="14.5px"
                    fontWeight="700"
                    leftIcon={<Icon as={FaShoppingCart} />}
                    _hover={{ bg: "#1E293B", transform: "translateY(-1px)", shadow: "md" }}
                    _active={{ transform: "translateY(0)" }}
                    onClick={handleAddToCart}
                    isLoading={isAddingToCart}
                  >
                    Add to Cart
                  </Button>

                  <Button
                    flex="1"
                    size="lg"
                    h="52px"
                    bg="linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)"
                    color="gray.950"
                    rounded="xl"
                    fontSize="14.5px"
                    fontWeight="800"
                    leftIcon={<Icon as={FaBolt} />}
                    _hover={{ filter: "brightness(1.08)", transform: "translateY(-1px)", shadow: "md" }}
                    _active={{ transform: "translateY(0)" }}
                    onClick={handleBuyNow}
                    isLoading={isAddingToCart}
                  >
                    Buy Now
                  </Button>
                </>
              ) : (
                <Box
                  w="100%"
                  p="4"
                  bg="white"
                  border="1px solid #E2E8F0"
                  rounded="xl"
                  shadow="sm"
                  textAlign="center"
                >
                  <Text fontSize="14px" fontWeight="600" color="gray.800" mb="2.5">
                    Please Sign In to purchase this publication
                  </Text>
                  <Box display="inline-block">
                    <Link to="/login">
                      <Button
                        size="md"
                        bg="yellow.500"
                        color="gray.950"
                        fontWeight="700"
                        rounded="lg"
                        _hover={{ bg: "yellow.400", transform: "translateY(-1px)", shadow: "md" }}
                        transition="all 0.2s"
                      >
                        Sign In to Purchase
                      </Button>
                    </Link>
                  </Box>
                </Box>
              )}
            </Flex>
          </Box>

          {/* Right Column: Title, Metadata, Pricing, Guarantees, Accordion */}
          <Box>
            {/* Category Pill */}
            {item.maincatname && (
              <Badge colorScheme="blue" variant="subtle" px="2.5" py="1" rounded="md" fontSize="11px" fontWeight="bold" textTransform="uppercase" mb="2">
                {item.maincatname} {item.subcatname ? `• ${item.subcatname}` : ""}
              </Badge>
            )}

            {/* Title */}
            <Text fontSize={{ base: "20px", md: "26px" }} fontWeight="800" color="gray.900" lineHeight="1.3" mb="3">
              {item.booktitle}
            </Text>

            {/* Author */}
            {item.author && (
              <Text fontSize="14px" color="gray.600" mb="3">
                Authored by: <Text as="span" fontWeight="bold" color="gray.900">{item.author}</Text>
              </Text>
            )}

            {/* Ratings & Reviews */}
            <Flex align="center" gap="3" mb="5">
              <Flex align="center" gap="1" bg="green.600" color="white" px="2.5" py="0.5" rounded="md">
                <Icon as={FaStar} boxSize="11px" />
                <Text fontSize="12.5px" fontWeight="bold">
                  {item.stars || "4.8"}
                </Text>
              </Flex>
              <Text fontSize="13px" color="gray.500">
                Verified Legal Publication Rating
              </Text>
            </Flex>

            {/* Pricing Card */}
            <Box bg="white" border="1px solid #E2E8F0" rounded="2xl" p="5" mb="6" shadow="sm">
              <Text fontSize="12px" fontWeight="700" color="green.600" textTransform="uppercase" letterSpacing="0.5px" mb="1">
                Special Pricing Offer
              </Text>

              <Flex align="baseline" gap="3" wrap="wrap">
                <Text fontSize={{ base: "28px", md: "34px" }} fontWeight="800" color="gray.900">
                  ₹{item.descprice}
                </Text>

                {originalPrice > descPrice && (
                  <>
                    <Text fontSize="18px" color="gray.400" as="del">
                      ₹{item.originalprice}
                    </Text>
                    <Badge colorScheme="green" fontSize="13px" px="2" py="0.5" rounded="md" fontWeight="bold">
                      {discountPct}% OFF
                    </Badge>
                  </>
                )}
              </Flex>

              {originalPrice > descPrice && (
                <Text fontSize="13px" color="green.700" fontWeight="600" mt="1.5">
                  You save ₹{originalPrice - descPrice} on this edition
                </Text>
              )}
            </Box>

            {/* Guarantees Grid */}
            <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3.5" mb="6">
              <Flex align="center" gap="3" p="3.5" bg="white" border="1px solid #E2E8F0" rounded="xl">
                <Icon as={FaUndo} color="#2563EB" boxSize="18px" />
                <Box>
                  <Text fontSize="13px" fontWeight="bold" color="gray.900">10-Day Easy Returns</Text>
                  <Text fontSize="11.5px" color="gray.500">Printing/binding defect guarantee</Text>
                </Box>
              </Flex>

              <Flex align="center" gap="3" p="3.5" bg="white" border="1px solid #E2E8F0" rounded="xl">
                <Icon as={FaTruck} color="#2563EB" boxSize="18px" />
                <Box>
                  <Text fontSize="13px" fontWeight="bold" color="gray.900">Express Delivery</Text>
                  <Text fontSize="11.5px" color="gray.500">Delivered within 3 - 7 court days</Text>
                </Box>
              </Flex>

              <Flex align="center" gap="3" p="3.5" bg="white" border="1px solid #E2E8F0" rounded="xl">
                <Icon as={FaFileInvoice} color="#2563EB" boxSize="18px" />
                <Box>
                  <Text fontSize="13px" fontWeight="bold" color="gray.900">GST Invoice Provided</Text>
                  <Text fontSize="11.5px" color="gray.500">Input tax credit eligible</Text>
                </Box>
              </Flex>

              <Flex align="center" gap="3" p="3.5" bg="white" border="1px solid #E2E8F0" rounded="xl">
                <Icon as={FaShieldAlt} color="#2563EB" boxSize="18px" />
                <Box>
                  <Text fontSize="13px" fontWeight="bold" color="gray.900">100% Genuine Edition</Text>
                  <Text fontSize="11.5px" color="gray.500">Directly from verified publishers</Text>
                </Box>
              </Flex>
            </Grid>

            {/* Specifications Accordion */}
            <Box bg="white" border="1px solid #E2E8F0" rounded="2xl" overflow="hidden" shadow="sm">
              <Accordion defaultIndex={[0]} allowMultiple>
                <AccordionItem border="none">
                  <h2>
                    <AccordionButton py="4" px="5" _hover={{ bg: "gray.50" }}>
                      <Box flex="1" textAlign="left" fontSize="16px" fontWeight="700" color="gray.900">
                        Publication Specifications & Metadata
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel px="5" pb="6">
                    <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="4" fontSize="13.5px">
                      {item.author && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Author</Text>
                          <Text fontWeight="600" color="gray.900">{item.author}</Text>
                        </Box>
                      )}
                      {item.publisher && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Publisher</Text>
                          <Text fontWeight="600" color="gray.900">{item.publisher}</Text>
                        </Box>
                      )}
                      {item.format && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Binding Format</Text>
                          <Text fontWeight="600" color="gray.900">{item.format}</Text>
                        </Box>
                      )}
                      {item.pagess && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Number of Pages</Text>
                          <Text fontWeight="600" color="gray.900">{item.pagess}</Text>
                        </Box>
                      )}
                      {item.language && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Language</Text>
                          <Text fontWeight="600" color="gray.900">{item.language}</Text>
                        </Box>
                      )}
                      {item.isbn && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">ISBN Number</Text>
                          <Text fontWeight="600" color="gray.900">{item.isbn}</Text>
                        </Box>
                      )}
                      {item.year && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Publication Year</Text>
                          <Text fontWeight="600" color="gray.900">{item.year}</Text>
                        </Box>
                      )}
                      {item.jurisdiction && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Jurisdiction</Text>
                          <Text fontWeight="600" color="gray.900">{item.jurisdiction}</Text>
                        </Box>
                      )}
                      {item.volume && (
                        <Box>
                          <Text color="gray.500" fontSize="12px">Volume</Text>
                          <Text fontWeight="600" color="gray.900">{item.volume}</Text>
                        </Box>
                      )}
                    </Grid>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
}

export default Viewpage;
